import React, { useCallback, useState, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import jsonData from '../playerjson'
import { loadLogixSDK } from "./logixPlayerSDK"
import { useTrackAnalytics, usePlayedAnalytics,usePlayerMatrics } from 'shared-ui/hooks'
import { CMS_EVENTS } from 'shared-ui/utils/gaConstants'

export default function PlayerInit({
  videoUrl,
  playBackdata,
  className = '',
  style = {},
  muted = false,
  autoplay = false,
  poster = '',
  playerId = 'logixPlayer',
  onPlayerReady,
  isActive = true,
  playVideoInLoop = true,
  activeIndex

}) {
  const logixPlayerRef = useRef(null)
  const playerobjRef = useRef(null)
  const playerBuilderRef = useRef(null)
  const containerRef = useRef(null)
  const endApiCalledRef = useRef(false)
  const startApiCalledRef = useRef(false)
  const [playerData, setPlayerData] = useState()
  const [isPlayerReady, setIsPlayerReady] = useState(false)
  const [autoplayAttempted, setAutoplayAttempted] = useState(false)
  const [showPlayButton, setShowPlayButton] = useState(false)
  const [playerobj, setPlayerobj] = useState(null)
  const { sendEvent } = useTrackAnalytics()
  const { sendPlayedEvent } = usePlayedAnalytics()
  const { sendPlayedMatricsEvent } = usePlayerMatrics()
  const wasActiveRef = useRef(false)
  const videoMetricsRef = useRef({
    duration: 0,
    currentTime: 0
  })
  const contentRef = useRef(null)
  const createPlayerObject = useCallback((logixPlayer) => {
    logixPlayer.createPlayer(jsonData)
  }, [])
  const playerMetricsRef = useRef({
    startupTime: 0,
    startupStartedAt: 0
  });
  const playbackStartTimeRef = useRef(0)
  const bufferingStartTimeRef = useRef(0)

  const accumulatedWatchTimeRef = useRef(0)
  const accumulatedBufferTimeRef = useRef(0)

  useEffect(() => {
    setPlayerData(playBackdata)
    contentRef.current = playBackdata
  }, [playBackdata])

  // useEffect(() => {
  //     if (!logixPlayerRef.current) {
  //         logixPlayerRef.current = new window.LogixPlayerComponent()
  //         createPlayerObject(logixPlayerRef.current)
  //     }
  // }, [createPlayerObject])

  useEffect(() => {
    let mounted = true;

    async function initPlayer() {
      await loadLogixSDK();

      if (!mounted) return;

      if (!logixPlayerRef.current) {
        logixPlayerRef.current =
          new window.LogixPlayerComponent();

        createPlayerObject(logixPlayerRef.current);
      }
    }

    initPlayer();

    return () => {
      mounted = false;
    };
  }, [createPlayerObject]);

  // Function to attempt autoplay with user interaction
  const attemptAutoplay = useCallback(
    (playerobj) => {
      if (!autoplay) return

      // Check if device is iPad/tablet
      const isTablet = /iPad|Android(?!.*Mobile)|Tablet/i.test(
        navigator.userAgent
      )

      // Try multiple methods to play the video
      const tryPlayVideo = () => {
        // Method 1: Try playerObj.player.play() (original method)
        if (playerobj && playerobj.player && playerobj.player.play) {
          try {
            playerobj.player
              .play()
              .then(() => {
                setShowPlayButton(false)
              })
              .catch((error) => {
                // Continue to next method
                tryNextMethod()
              })
          } catch (error) {
            tryNextMethod()
          }
        } else {
          tryNextMethod()
        }
      }

      const tryNextMethod = () => {
        // Method 2: Try direct playerObj.play()
        if (playerobj && playerobj.play) {
          try {
            playerobj
              .play()
              .then(() => {
                setShowPlayButton(false)
              })
              .catch((error) => {
                // Continue to next method
                tryVideoElement()
              })
          } catch (error) {
            tryVideoElement()
          }
        } else {
          tryVideoElement()
        }
      }

      const tryVideoElement = () => {
        // Method 3: Try direct video element
        const videoElement = containerRef.current?.closest(`#${playerId}`)?.querySelector('video')

        if (videoElement && !autoplayAttempted) {
          setAutoplayAttempted(true)
          videoElement.muted = muted // Ensure muted for autoplay
          videoElement.loop = playVideoInLoop // Ensure loop is set

          videoElement
            .play()
            .then(() => {
              setShowPlayButton(false)
            })
            .catch((error) => {
              if (!muted) setShowPlayButton(true)
              // Add click listener to the entire document to enable autoplay
              const enableAutoplay = () => {
                if (videoElement && videoElement.play) {
                  videoElement
                    .play()
                    .then(() => {
                      setShowPlayButton(false)
                    })
                    .catch((e) => {
                      // Autoplay still failed
                    })
                }
                document.removeEventListener(
                  'click',
                  enableAutoplay
                )
                document.removeEventListener(
                  'touchstart',
                  enableAutoplay
                )
              }
              document.addEventListener('click', enableAutoplay)
              document.addEventListener(
                'touchstart',
                enableAutoplay
              )
            })
        } else {
          // No video element found
        }
      }

      // Start with the first method
      if (isTablet) {
        setTimeout(tryPlayVideo, 500)
      } else {
        tryPlayVideo()
      }
    },
    [autoplay, autoplayAttempted, muted, playerId]
  )

  // Manual play function
  const handlePlayClick = () => {
    if (playerobj && playerobj.play) {
      playerobj
        .play()
        .then(() => {
          setShowPlayButton(false)
        })
        .catch((e) => {
          // Manual play failed
        })
    }
  }
  const getVideoMetrics = () => {
    const videoElement = containerRef.current
      ?.closest(`#${playerId}`)
      ?.querySelector('video')

    return {
      duration:
        videoElement &&
          Number.isFinite(videoElement.duration) &&
          videoElement.duration > 0
          ? Math.round(videoElement.duration)
          : null,
      currentTime: Math.round(videoElement?.currentTime || 0)
    }
  }
  useEffect(() => {
    startApiCalledRef.current = false
    endApiCalledRef.current = false
  }, [videoUrl])

  const fireContentStart = () => {
    if (startApiCalledRef.current) return

    const { duration } = getVideoMetrics()
    startApiCalledRef.current = true

    sendEvent(CMS_EVENTS.VIDEO_CONTENT_START, {
      contentId: playBackdata?.content_id,
      contentTitle: playBackdata?.title,
      contentType: playBackdata?.content_type,
      language: playBackdata?.language,
      duration
    })
  }

  const fireContentEnd = () => {
    if (endApiCalledRef.current) return
    endApiCalledRef.current = true
    const { duration, currentTime } = getVideoMetrics()
    sendEvent(CMS_EVENTS.VIDEO_CONTENT_END, {
      contentId: playBackdata?.content_id,
      contentTitle: playBackdata?.title,
      contentType: playBackdata?.content_type,
      language: playBackdata?.language,
      duration,
      watchTime: currentTime
    })
  }

  const handleSendPlayerMetrics = () => {
    const payload = {
      content_id: playBackdata?.content_id,
      buffering_rate: getBufferingRate(),
      bitrate: -1,
      startup_time: Number(
        playerMetricsRef.current.startupTime
      ),
      exit_before_start: 0
    }

    sendPlayedMatricsEvent(payload)
  }

  const getBufferingRate = () => {
    const activeWatchTime =
      playbackStartTimeRef.current > 0
        ? Date.now() - playbackStartTimeRef.current
        : 0

    const activeBufferTime =
      bufferingStartTimeRef.current > 0
        ? Date.now() - bufferingStartTimeRef.current
        : 0

    const watchTime =
      accumulatedWatchTimeRef.current +
      activeWatchTime

    const bufferingTime =
      accumulatedBufferTimeRef.current +
      activeBufferTime

    const totalSessionTime =
      watchTime + bufferingTime

    return totalSessionTime > 0
      ? Number(
        (
          bufferingTime /
          totalSessionTime
        ).toFixed(4)
      )
      : 0
  }


  useEffect(() => {
    if (wasActiveRef.current && isActive === false) {
      const content = contentRef.current

      sendPlayedEvent(CMS_EVENTS.VIDEO_CONTENT_PLAYED, {
        contentId: content?.content_id,
        contentTitle: content?.title,
        contentType: content?.content_type,
        language: content?.language,
        duration: videoMetricsRef.current.duration,
        watchTime: videoMetricsRef.current.currentTime
      })
    }

    if (isActive === false) {
      const playerContainer =
        containerRef.current?.closest(`#${playerId}`) ||
        (playerId ? document.getElementById(playerId) : null)
      if (playerContainer) {
        playerContainer.querySelectorAll('video, audio').forEach((el) => {
          try {
            el.pause()
            el.muted = true
          } catch (e) {}
        })
      }
      if (playerobjRef.current?.player && typeof playerobjRef.current.player.pause === 'function') {
        try {
          playerobjRef.current.player.pause()
          playerobjRef.current.player.muted?.(true)
        } catch (e) {}
      }
      if (playerobjRef.current && typeof playerobjRef.current.pause === 'function') {
        try { playerobjRef.current.pause() } catch (e) {}
      }
    }

    wasActiveRef.current = isActive !== false
  }, [isActive, playerId])

  useEffect(() => {
    if (!videoUrl) return

    let mounted = true;

    async function setupPlayer() {
      await loadLogixSDK();

      if (!mounted) return;
      if (!window.playerBuilder) return;

      var playBackData = {
        autoplay: autoplay,
        muted: muted,
        videoUrl: videoUrl,
        playerControlsLayout: jsonData,
        dataSource:
          navigator.userAgent.indexOf('Tizen') > -1
            ? 'com.viacom18.media.ondemand.tizen'
            : 'com.viacom18.media.ondemand.webos',
        vendorName: 'logituit_'
      };

      var assetMetaData = {
        isLive: false,
        isDvr: false,
        isEncrypted: false,
        thumbnailUrl: poster
      };

      if (videoUrl.includes("mp4")) {
        playBackData.type = "video/mp4";
      } else if (videoUrl.includes("mpd")) {
        playBackData.type = "application/dash+xml";
      } else if (videoUrl.includes("m3u8")) {
        playBackData.type = "application/x-mpegURL";
      }

      playerBuilderRef.current =
        new window.playerBuilder();

      playerBuilderRef.current.setPlaybackData(playBackData);
      playerBuilderRef.current.setAssetMetaData(assetMetaData);

      if (logixPlayerRef.current) {
        logixPlayerRef.current
          .initializePlayer(playerId, playerBuilderRef.current)
          .then(function (playerobj) {
            setIsPlayerReady(true);
            setPlayerobj(playerobj);
            playerobjRef.current = playerobj;
            const videoElement = containerRef.current?.closest(`#${playerId}`)?.querySelector('video');

            onPlayerReady?.(videoElement || null);

            if (autoplay && isActive !== false) {
              attemptAutoplay(playerobj);
            } else {
              // Explicitly prevent SDK from continuing playback
              if (videoElement) {
                try { videoElement.pause(); } catch (e) {}
                videoElement.autoplay = false;
                if (isActive === false) {
                  videoElement.muted = true;
                }
              }

              if (playerobj?.player?.pause) {
                try { playerobj.player.pause(); } catch (e) {}
              }

              if (playerobj?.pause) {
                try { playerobj.pause(); } catch (e) {}
              }
            }
          });
      }
    }

    setupPlayer();

    return () => {
      mounted = false;

      if (playerBuilderRef.current?.destroy) {
        playerBuilderRef.current.destroy()
      }
      onPlayerReady?.(null)
    };
  }, [videoUrl, autoplay, playerId]);

  // Add a global click handler to enable autoplay after user interaction
  useEffect(() => {
    if (!autoplay) return
    const handleUserInteraction = () => {
      if (isPlayerReady && !autoplayAttempted) {
        const videoElement = containerRef.current
          ?.closest(`#${playerId}`)
          ?.querySelector('video')
        if (videoElement && videoElement.play) {
          videoElement
            .play()
            .then(() => {
              setAutoplayAttempted(true)
              setShowPlayButton(false)
            })
            .catch((e) => {
              // Autoplay still failed
            })
        }
      }
    }
    document.addEventListener('click', handleUserInteraction)
    document.addEventListener('touchstart', handleUserInteraction)
    return () => {
      document.removeEventListener('click', handleUserInteraction)
      document.removeEventListener('touchstart', handleUserInteraction)
    }
  }, [isPlayerReady, autoplayAttempted, autoplay, playerId])

  useEffect(() => {
    const playerContainer =
      containerRef.current?.closest(`#${playerId}`) ||
      containerRef.current?.closest('#playerDiv') ||
      (playerId ? document.getElementById(playerId) : null)

    if (playerContainer) {
      const mediaElements = playerContainer.querySelectorAll('video, audio')
      mediaElements.forEach((el) => {
        try { el.muted = muted } catch (e) {}
      })
    }
    if (playerobjRef.current?.player && typeof playerobjRef.current.player.muted === 'function') {
      try { playerobjRef.current.player.muted(muted) } catch (e) {}
    }
  }, [muted, isPlayerReady, playerId])

  useEffect(() => {
    const videoElement = containerRef.current
      ?.closest(`#${playerId}`)
      ?.querySelector('video')
    playerMetricsRef.current.startupStartedAt = performance.now()

    let handlePlay
    let handleTimeUpdate
    let handleEnded
    let handleWaiting
    let handlePlaying

    let handleLoadedData
    if (isPlayerReady) {
      // Video element in useEffect

      if (videoElement) {
        videoElement.loop = playVideoInLoop
        handlePlay = () => {
          fireContentStart()

          if (!playbackStartTimeRef.current) {
            playbackStartTimeRef.current = Date.now()
          }
        }
        handlePlaying = () => {
          if (bufferingStartTimeRef.current) {
            accumulatedBufferTimeRef.current +=
              Date.now() - bufferingStartTimeRef.current

            bufferingStartTimeRef.current = 0
          }

          playbackStartTimeRef.current = Date.now()
        }

        handleEnded = () => {
          fireContentEnd()
          handleSendPlayerMetrics()
        }

        handleLoadedData = () => {
          playerMetricsRef.current.startupTime =
            (
              (performance.now() -
                playerMetricsRef.current.startupStartedAt) /
              1000
            ).toFixed(2)
        }

        handleWaiting = () => {
          if (!bufferingStartTimeRef.current) {
            bufferingStartTimeRef.current = Date.now()
          }

          if (playbackStartTimeRef.current) {
            accumulatedWatchTimeRef.current +=
              Date.now() - playbackStartTimeRef.current

            playbackStartTimeRef.current = 0
          }
        }

        handleTimeUpdate = () => {
          const duration = videoElement.duration || 0
          const currentTime = videoElement.currentTime || 0

          videoMetricsRef.current = {
            duration: Math.round(duration),
            currentTime: Math.round(currentTime)
          }

          // bitrate calculation


          if (
            duration > 0 &&
            currentTime >= duration - 0.25 &&
            !endApiCalledRef.current
          ) {
            fireContentEnd()
            handleSendPlayerMetrics()
          }
        }

        videoElement.addEventListener('timeupdate', handleTimeUpdate)
        videoElement.addEventListener('play', handlePlay)
        videoElement.addEventListener('ended', handleEnded)
        videoElement.addEventListener('loadeddata', handleLoadedData)
        videoElement.addEventListener('waiting', handleWaiting)
        videoElement.addEventListener('playing', handlePlaying)

        // Initialize analytics
        if (autoplay) {
          videoElement.muted = muted

          const isTablet = /iPad|Android(?!.*Mobile)|Tablet/i.test(
            navigator.userAgent
          )

          const playVideo = () => {
            videoElement
              .play()
              .then(() => {
                setShowPlayButton(false)
              })
              .catch(() => {
                if (!muted) setShowPlayButton(true)

                setTimeout(() => {
                  if (videoElement && videoElement.play) {
                    videoElement
                      .play()
                      .then(() => {
                        setShowPlayButton(false)
                      })
                      .catch(() => { })
                  }
                }, 1000)
              })
          }

          if (isTablet) {
            setTimeout(playVideo, 1000)
          } else {
            playVideo()
          }
        }
      } else {
        // Try to find video element after a delay for tablets
        const isTablet = /iPad|Android(?!.*Mobile)|Tablet/i.test(
          navigator.userAgent
        )
        if (isTablet && autoplay) {
          setTimeout(() => {
            const delayedVideoElement = containerRef.current
              ?.closest(`#${playerId}`)
              ?.querySelector('video')

            if (delayedVideoElement) {
              delayedVideoElement.loop = true
              delayedVideoElement.muted = muted
              delayedVideoElement
                .play()
                .then(() => {
                  setShowPlayButton(false)
                })
                .catch((error) => {
                  // Delayed tablet video autoplay failed
                })
            }
          }, 2000)
        }
      }
    }

    // Expose player object globally for external access
    if (playerobj && isPlayerReady) {
      window.splashPlayerObj = playerobj

      // Create a helper function to play video that works on both platforms
      window.playSplashVideo = () => {
        // Check if player object exists and is ready
        if (!playerobj || !isPlayerReady) {
          return Promise.reject(new Error('Player not ready'))
        }

        // Try multiple methods to play the video
        return new Promise((resolve, reject) => {
          // Method 1: Try playerObj.player.play() (original method)
          if (playerobj && playerobj.player && playerobj.player.play) {
            try {
              playerobj.player
                .play()
                .then(() => {
                  resolve()
                })
                .catch((error) => {
                  // Continue to next method
                  tryNextMethod()
                })
            } catch (error) {
              tryNextMethod()
            }
          } else {
            tryNextMethod()
          }

          const tryNextMethod = () => {
            // Method 2: Try direct playerObj.play()
            if (playerobj && playerobj.play) {
              try {
                playerobj
                  .play()
                  .then(() => {
                    resolve()
                  })
                  .catch((error) => {
                    // Continue to next method
                    tryVideoElement()
                  })
              } catch (error) {
                tryVideoElement()
              }
            } else {
              tryVideoElement()
            }
          }

          const tryVideoElement = () => {
            // Method 3: Try direct video element
            const videoElement = containerRef.current
              ?.closest(`#${playerId}`)
              ?.querySelector('video')
            if (videoElement) {
              videoElement.muted = muted
              videoElement.loop = playVideoInLoop
              videoElement
                .play()
                .then(() => {
                  resolve()
                })
                .catch((error) => {
                  reject(new Error('All play methods failed'))
                })
            } else {
              reject(new Error('No video element available'))
            }
          }
        })
      }
    }

    // Cleanup function - only remove listeners during dynamic re-renders
    return () => {
      if (videoElement) {
        videoElement.removeEventListener('timeupdate', handleTimeUpdate)
        videoElement.removeEventListener('play', handlePlay)
        videoElement.removeEventListener('ended', handleEnded)
        videoElement.removeEventListener('loadeddata', handleLoadedData)
        videoElement.removeEventListener('playing', handlePlaying)
        videoElement.removeEventListener('waiting', handleWaiting)
      }
    }
  }, [isPlayerReady, autoplay, muted, playerobj, playerId])

  // Dedicated unmount teardown effect to prevent background audio leakage
  useEffect(() => {
    return () => {
      if (playerobjRef.current) {
        try {
          if (typeof playerobjRef.current.pause === 'function') playerobjRef.current.pause()
          if (playerobjRef.current.player && typeof playerobjRef.current.player.pause === 'function') playerobjRef.current.player.pause()
          if (typeof playerobjRef.current.destroy === 'function') playerobjRef.current.destroy()
          if (playerobjRef.current.player && typeof playerobjRef.current.player.destroy === 'function') playerobjRef.current.player.destroy()
        } catch (e) {}
        playerobjRef.current = null
      }

      if (logixPlayerRef.current) {
        try {
          if (typeof logixPlayerRef.current.pause === 'function') logixPlayerRef.current.pause()
          if (typeof logixPlayerRef.current.destroy === 'function') logixPlayerRef.current.destroy()
        } catch (e) {}
        logixPlayerRef.current = null
      }

      if (window.splashPlayerObj) {
        try {
          if (window.splashPlayerObj.player && typeof window.splashPlayerObj.player.pause === 'function') {
            window.splashPlayerObj.player.pause()
          }
          if (window.splashPlayerObj.media && typeof window.splashPlayerObj.media.pause === 'function') {
            window.splashPlayerObj.media.pause()
          }
          if (typeof window.splashPlayerObj.pause === 'function') window.splashPlayerObj.pause()
          if (typeof window.splashPlayerObj.destroy === 'function') window.splashPlayerObj.destroy()
        } catch (e) {}
        delete window.playSplashVideo
        delete window.splashPlayerObj
      }

      try {
        const playerContainer =
          containerRef.current?.closest(`#${playerId}`) ||
          containerRef.current?.closest('#playerDiv') ||
          (playerId ? document.getElementById(playerId) : null)

        if (playerContainer) {
          const elements = playerContainer.querySelectorAll('video, audio')
          elements.forEach((v) => {
            try { v.pause() } catch (e) {}
            try { v.currentTime = 0 } catch (e) {}
            try { v.removeAttribute('src') } catch (e) {}
            try { v.src = '' } catch (e) {}
            try { v.load() } catch (e) {}
            try { v.remove() } catch (e) {}
          })
        }
      } catch (e) {}

      playerMetricsRef.current = {
        startupTime: 0,
        startupStartedAt: 0
      }
      playbackStartTimeRef.current = 0
      bufferingStartTimeRef.current = 0

      accumulatedWatchTimeRef.current = 0
      accumulatedBufferTimeRef.current = 0
    }
  }, [playerId])

  return <div ref={containerRef} className={className} style={style}></div>
}
